import { Dispatch, Fragment, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import PageContent from 'components/PageContent';
import Countdown from 'components/CountDown';
import { IFlightTicketProposal } from 'entities/Flights/sdk.ts';
import { useTranslation } from 'react-i18next';
import Modal from 'components/Modal';
import { Field, FieldGroup, FieldLabel } from 'components/ui/field';
import { Input } from 'components/ui/input';
import TotalSection from 'pages/FlightOffer/TotalSection';
import { FlightOfferViewEnum } from 'pages/FlightOffer/constants.tsx';
import { KeyedMutator } from 'swr';
import { sendInvoiceDetails, useCountries, useInvoiceDetails } from 'api/InvoiceDetails/sdk.ts';
import { useLocation, useNavigate, useOutletContext, useParams } from 'react-router';
import * as yup from 'yup';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { H2, H4 } from 'components/ui/typography.tsx';
import { Button } from 'components/ui/button.tsx';
import { MapPinIcon, UserIcon } from '@heroicons/react/20/solid';
import { cn } from 'utils/utils.ts';
import { emailAtom } from 'pages/FlightOffer/EmailAtom';
import { useAtomValue } from 'jotai';
import { t } from 'i18next';
import { Combobox } from '@/pages/FlightOffer/InvoiceDetailsView/combobox';
import { useAuth } from '@/entities/Auth';

interface InvoiceDetailsViewProps {
  flightTicketProposal: IFlightTicketProposal;
  currency: string | null;
  setCurrency: Dispatch<SetStateAction<string | null>>;
  flightTicketProposalMutate: KeyedMutator<IFlightTicketProposal>;
}

const schema = yup.object({
  first_name: yup.string().required(() => t('common.requiredField')),
  last_name: yup.string().required(() => t('common.requiredField')),
  phone: yup.string().required(() => t('common.requiredField')),
  street: yup.string().required(() => t('common.requiredField')),
  house_number: yup.string().required(() => t('common.requiredField')),
  zipcode: yup.string().required(() => t('common.requiredField')),
  city: yup.string().required(() => t('common.requiredField')),
  country_code: yup
    .string()
    .nullable()
    .required(() => t('common.requiredField')),
  email: yup
    .string()
    .email()
    .required(() => t('common.emailIsRequiredLabel')),
});

interface InvoiceDetailsData {
  first_name: string;
  last_name: string;
  phone: string;
  street: string;
  house_number: string;
  zipcode: string;
  city: string;
  country_code: string;
  email: string;
}

export const InvoiceDetailsView = () => {
  const { flightTicketProposal, currency, setCurrency, flightTicketProposalMutate } =
    useOutletContext<InvoiceDetailsViewProps>();
  const { t } = useTranslation();
  const [isExpiredModalOpen, setIsExpiredModalOpen] = useState(false);
  const [isInvoiceDataLoading, setIsInvoiceDataLoading] = useState(false);
  const params = useParams<{ flightTicketRequestUUID: string }>();
  const navigate = useNavigate();
  const emailFromAtom = useAtomValue(emailAtom);
  const locationState = useLocation().state ?? {};
  const { userInfo } = useAuth();
  const email = emailFromAtom ?? locationState.email ?? userInfo?.email;
  const { data } = useInvoiceDetails(params.flightTicketRequestUUID!, email!);
  const isOfferExpired = new Date(flightTicketProposal.expiration_datetime) < new Date();
  const { data: countries } = useCountries();
  const countryOptions = useMemo(
    () => countries?.map((country) => ({ value: country.code2, label: country.name })) ?? [],
    [countries]
  );
  const countryOptionsByCode = useMemo(
    () => new Map(countryOptions.map((option) => [option.value.trim().toUpperCase(), option])),
    [countryOptions]
  );

  const {
    formState: { isValid, errors, touchedFields },
    register,
    reset,
    getValues,
    control,
  } = useForm<InvoiceDetailsData>({
    resolver: yupResolver(schema),
    mode: 'onTouched',
    defaultValues: {
      first_name: '',
      last_name: '',
      phone: '',
      street: '',
      house_number: '',
      zipcode: '',
      city: '',
      country_code: '',
      email: '',
    },
  });

  useEffect(() => {
    if (!data || !email) {
      return;
    }

    reset({
      first_name: data.invoice_details.first_name,
      last_name: data.invoice_details.last_name,
      phone: data.invoice_details.phone,
      street: data.invoice_details.street,
      house_number: data.invoice_details.house_number,
      zipcode: data.invoice_details.zipcode,
      city: data.invoice_details.city,
      country_code: data.invoice_details.country_code,
      email,
    });
  }, [data, email, reset]);

  const handleRefreshClick = useCallback(() => {
    window.location.reload();
  }, []);

  const handleCountdownExpire = useCallback(() => {
    setIsExpiredModalOpen(true);
  }, []);

  const handleConfirmAndProceedClick = useCallback(async () => {
    if (isOfferExpired || isInvoiceDataLoading) {
      return;
    }

    try {
      setIsInvoiceDataLoading(true);

      const invoiceDetails = getValues();

      await sendInvoiceDetails(params.flightTicketRequestUUID!, invoiceDetails);

      navigate(`/flights/offer/${params.flightTicketRequestUUID}/payment-methods`);
    } catch (error) {
      console.error(error);
    } finally {
      setIsInvoiceDataLoading(false);
    }
  }, [getValues, isInvoiceDataLoading, isOfferExpired, navigate, params.flightTicketRequestUUID]);

  return (
    <Fragment>
      <PageContent>
        <div className='flex justify-between md:items-center flex-col md:flex-row'>
          <H2>{t('invoiceDetails.invoiceDetails')}</H2>
          <span className='font-[MuseoSansCyrl700] flex md:text-lg md:flex-col max-[300px]:flex-col'>
            <span>{t('flightOffer.offerExpiresIn')}:&nbsp;</span>
            <Countdown
              targetTs={new Date(flightTicketProposal.expiration_datetime).getTime()}
              onExpire={handleCountdownExpire}
            />
          </span>
        </div>
        <div className='w-full bg-lightBeige mt-10 p-10 max-md:p-5 rounded-lg'>
          <FieldGroup>
            <div className='flex flex-col gap-y-5 md:flex-row md:flex-wrap'>
              <div className='order-1 flex items-center gap-2 md:w-1/2 md:pr-5'>
                <UserIcon className='h-6 w-6 text-content-secondary' />
                <H4 className={cn('font-[MuseoSansCyrl500]')}>{t('invoiceDetails.personalInformation')}</H4>
              </div>
              <Field className='order-2 md:order-3 md:w-1/2 md:pr-5'>
                <FieldLabel>{t('invoiceDetails.firstName')}</FieldLabel>
                <Input
                  className='min-w-0'
                  isTouched={touchedFields.first_name}
                  error={errors.first_name}
                  {...register('first_name')}
                  type='text'
                  placeholder='e.g. Elias'
                />
              </Field>
              <Field className='order-3 md:order-5 md:w-1/2 md:pr-5'>
                <FieldLabel>{t('invoiceDetails.lastName')}</FieldLabel>
                <Input
                  className='min-w-0'
                  isTouched={touchedFields.last_name}
                  error={errors.last_name}
                  {...register('last_name')}
                  type='text'
                  placeholder='e.g. Van der Berg'
                />
              </Field>
              <Field className='order-4 md:order-7 md:w-1/2 md:pr-5'>
                <FieldLabel>{t('invoiceDetails.phoneNumber')}</FieldLabel>
                <Input
                  className='min-w-0'
                  isTouched={touchedFields.phone}
                  error={errors.phone}
                  {...register('phone')}
                  type='tel'
                  placeholder='+31 6 1234 5678'
                />
              </Field>
              <div className='order-5 flex items-center gap-2 w-full min-w-0 md:order-2 md:mt-0 md:w-1/2 md:pl-5'>
                <MapPinIcon className='h-6 w-6 text-content-secondary' />
                <H4 className={cn('font-[MuseoSansCyrl500]')}>{t('invoiceDetails.billingAddress')}</H4>
              </div>
              <div className='order-6 flex flex-col gap-3 w-full min-w-0 md:order-4 md:w-1/2 md:pl-5 sm:flex-row'>
                <Field className='w-full'>
                  <FieldLabel>{t('invoiceDetails.streetName')}</FieldLabel>
                  <Input
                    className='min-w-0'
                    isTouched={touchedFields.street}
                    error={errors.street}
                    {...register('street')}
                    placeholder='Serengeti Way'
                  />
                </Field>
                <Field className='w-full sm:w-[140px]'>
                  <FieldLabel className='text-nowrap'>{t('invoiceDetails.houseNumber')}</FieldLabel>
                  <Input
                    className='min-w-0'
                    isTouched={touchedFields.house_number}
                    error={errors.house_number}
                    {...register('house_number')}
                    type='text'
                    placeholder='12-A'
                  />
                </Field>
              </div>
              <div className='order-7 flex flex-col gap-3 w-full min-w-0 md:order-6 md:w-1/2 md:pl-5 sm:flex-row'>
                <Field className='sm:w-[140px]'>
                  <FieldLabel className='text-nowrap'>{t('invoiceDetails.zipCode')}</FieldLabel>
                  <Input
                    className='min-w-0'
                    isTouched={touchedFields.zipcode}
                    error={errors.zipcode}
                    {...register('zipcode')}
                    placeholder='1012 AB'
                  />
                </Field>
                <Field className='w-full min-w-0'>
                  <FieldLabel>{t('invoiceDetails.city')}</FieldLabel>
                  <Input
                    className='min-w-0'
                    isTouched={touchedFields.city}
                    error={errors.city}
                    {...register('city')}
                    type='text'
                    placeholder='Amsterdam'
                  />
                </Field>
              </div>
              <Field className='w-full min-w-0 order-8 md:w-1/2 md:pl-5'>
                <FieldLabel>{t('invoiceDetails.country')}</FieldLabel>
                <Controller
                  name='country_code'
                  control={control}
                  render={({ field }) => (
                    <Combobox
                      name={field.name}
                      ref={field.ref}
                      value={countryOptionsByCode.get(field.value) ?? null}
                      onValueChange={(option) => field.onChange(option?.value ?? '')}
                      onBlur={field.onBlur}
                      options={countryOptions}
                      placeholder={t('invoiceDetails.selectCountry')}
                      isTouched={touchedFields.country_code}
                      error={errors.country_code}
                    />
                  )}
                />
              </Field>
            </div>
          </FieldGroup>
        </div>
      </PageContent>
      <TotalSection
        view={FlightOfferViewEnum.INVOICE_DETAILS}
        isConfirmDisabled={!isValid || isInvoiceDataLoading}
        isConfirmLoading={isInvoiceDataLoading}
        flightTicketProposal={flightTicketProposal}
        handleConfirmClick={handleConfirmAndProceedClick}
        currency={currency}
        setCurrency={setCurrency}
        flightTicketProposalMutate={flightTicketProposalMutate}
        backButtonClick={() => navigate(`/flights/offer/${params.flightTicketRequestUUID}/invoice-details/email`)}
      />
      <Modal
        open={isExpiredModalOpen}
        title={t('flightOffer.offerHasExpired')}
        content={
          <div className='w-full'>
            <div className='flex justify-center'>
              <Button onClick={handleRefreshClick}>{t('common.refresh')}</Button>
            </div>
          </div>
        }
      />
    </Fragment>
  );
};
